import React, { useState, useRef, useEffect, useMemo } from 'react';
import './EntradaManual.css';
import bcGlEntries from '../data/bc-gl-entries.json';
import { fetchClientByPropertyId } from '../hooks/useHubSpot';
import { fetchDistinctTransactionCoachesByUniqueId } from '../services/dataService';

function formatBCDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatImporte(value) {
  return Math.abs(value).toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function Toast({ message, onHide }) {
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(onHide, 3800);
    return () => clearTimeout(t);
  }, [message, onHide]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className={`toast${message ? ' show' : ''}`}>{message}</div>
  );
}

function ProjectDropdown({ inputId, value, onChange, onSelect, placeholder, projectsData = [] }) {
  const [matches, setMatches] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const handleInput = (val) => {
    onChange(val);
    if (!val) { setOpen(false); return; }
    const q = val.toLowerCase();
    const m = projectsData.filter(p =>
      (p.name ?? '').toLowerCase().includes(q) ||
      (p.id ?? '').toLowerCase().includes(q) ||
      (p.operationName ?? '').toLowerCase().includes(q)
    );
    setMatches(m.slice(0, 12));
    setOpen(m.length > 0);
  };

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <input
        id={inputId}
        type="text"
        value={value}
        onChange={e => handleInput(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
      />
      {open && (
        <div className="proj-dropdown">
          {matches.map(p => (
            <div
              key={p.id}
              className="proj-option"
              onClick={() => { onSelect(p); setOpen(false); }}
            >
              <span style={{ fontWeight: 500 }}>{p.name}</span>
              <span style={{ fontSize: 10, color: 'var(--tx2)' }}>{p.id} · {p.type ?? '—'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EntradaManual({ projects = [] }) {
  const [activeTab, setActiveTab] = useState('cobro');
  const [toast, setToast] = useState('');

  // Cobros BC sin Unique ID: haber (Importe DL < 0 = dinero recibido de cliente)
  const pendingPayments = useMemo(() => {
    return (bcGlEntries.entries || [])
      .filter(e => (!e['Unique ID'] || e['Unique ID'].trim() === '') && e['Importe (DL)'] < 0)
      .map(e => ({
        fecha: formatBCDate(e['Fecha de registro']),
        fechaRaw: e['Fecha de registro'],
        importe: formatImporte(e['Importe (DL)']),
        importeNum: Math.abs(e['Importe (DL)']),
        concepto: e['Description'] || '—',
        noDocumento: e['No documento'] || '',
      }));
  }, []);

  // Cobro form state
  const [selPending, setSelPending] = useState(null);
  const [cProjSearch, setCProjSearch] = useState('');
  const [cProj, setCProj] = useState(null);
  const [cUnit, setCUnit] = useState('');
  const [cClient, setCClient] = useState('');
  const [cUid, setCUid] = useState('');
  const [cNotes, setCNotes] = useState('CONTACTAR PROPERTY COACH');
  const [clientLoading, setClientLoading] = useState(false);
  const [coachNames, setCoachNames] = useState([]);
  const [coachLookupLoading, setCoachLookupLoading] = useState(false);

  // Unidades sin cliente asignado (status 'Sin cliente' = sin Unique ID en Vistral/HubSpot)
  const pendingUnits = useMemo(() => {
    if (!cProj) return [];
    return (cProj.units ?? []).filter(u => u.status === 'Sin cliente' || (!u.client && !u.clientUniqueId));
  }, [cProj]);

  // Pago form state
  const [pProjSearch, setPProjSearch] = useState('');
  const [pImporte, setPImporte] = useState('');
  const [pFecha, setPFecha] = useState('');
  const [pTipo, setPTipo] = useState('');
  const [pDesc, setPDesc] = useState('');
  const [pVendedor, setPVendedor] = useState('');
  const [pNotas, setPNotas] = useState('');

  const handleSelectPending = (p) => {
    setSelPending(p);
    setCNotes(`CONTACTAR PROPERTY COACH\n\nCobro de €${p.importe} recibido el ${p.fecha}.\nConcepto BC (${p.noDocumento}): "${p.concepto}".\nCliente no incluyó el código 000+UniqueID.`);
  };

  const selectCProj = (proj) => {
    setCProj(proj);
    setCProjSearch(`${proj.name}  (${proj.id})`);
    setCUnit('');
  };

  const selectPProj = (proj) => {
    setPProjSearch(`${proj.name}  (${proj.id})`);
  };

  // When a unit is selected from the dropdown, pre-fill client data if available.
  // If the unit has no client yet, look it up live in HubSpot by propertyId.
  const handleUnitChange = async (unitId) => {
    setCUnit(unitId);
    setCClient('');
    setCUid('');
    if (!cProj || !unitId) return;

    const unit = (cProj.units ?? []).find(u => u.id === unitId || u.uniqueId === unitId);
    if (!unit) return;

    // If enriched data is already available, use it directly
    if (unit.client) {
      setCClient(unit.client);
      if (unit.clientUniqueId) setCUid(unit.clientUniqueId);
      else if (unit.uniqueId) setCUid(unit.uniqueId);
      return;
    }

    // Otherwise fetch from HubSpot using the property's uniqueId
    const propertyId = unit.uniqueId;
    if (!propertyId) return;

    setClientLoading(true);
    try {
      const result = await fetchClientByPropertyId(propertyId);
      if (result?.client) {
        setCClient(result.client);
        if (result.uniqueId) setCUid(result.uniqueId);
      }
    } catch (err) {
      console.warn('[HubSpot] No se pudo obtener el cliente:', err.message);
    } finally {
      setClientLoading(false);
    }
  };

  const clearForm = () => {
    setSelPending(null);
    setCProjSearch(''); setCProj(null); setCUnit('');
    setCClient(''); setCUid('');
    setCNotes('CONTACTAR PROPERTY COACH');
    setClientLoading(false);
    setCoachNames([]);
    setCoachLookupLoading(false);
  };

  useEffect(() => {
    const normalizedUid = cUid.trim();
    if (!normalizedUid) {
      setCoachNames([]);
      setCoachLookupLoading(false);
      return;
    }

    let cancelled = false;
    const lookupTimeout = setTimeout(async () => {
      setCoachLookupLoading(true);
      try {
        const coaches = await fetchDistinctTransactionCoachesByUniqueId(normalizedUid);
        if (!cancelled) setCoachNames(Array.isArray(coaches) ? coaches : []);
      } catch (err) {
        if (!cancelled) {
          setCoachNames([]);
          console.warn('[Airtable] No se pudo obtener el Property Coach por Unique ID:', err.message);
        }
      } finally {
        if (!cancelled) setCoachLookupLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(lookupTimeout);
    };
  }, [cUid]);

  const selectedUnit = useMemo(() => {
    if (!cProj || !cUnit) return null;
    return (cProj.units ?? []).find(u => u.id === cUnit || u.uniqueId === cUnit) ?? null;
  }, [cProj, cUnit]);

  const submitCobro = () => {
    if (!cClient || !cProj || !cUnit) {
      setToast('Rellena los campos obligatorios antes de asignar.');
      return;
    }
    setToast('Cobro asignado correctamente.\nDesaparece de la lista de pendientes y se refleja en la vista general.');
    clearForm();
  };

  const sendCoachEmail = () => {
    const coachNamesText = coachNames.length > 0 ? coachNames.join(', ') : 'Property Coach';
    const subject = encodeURIComponent(`Acción requerida: cliente sin código 000+UniqueID — ${cProj?.name || '[proyecto]'}`);
    const body = encodeURIComponent(`Hola,\n\nEl siguiente cliente ha realizado un pago sin incluir el código 000+UniqueID:\n\n• Property Coach: ${coachNamesText}\n• Cliente: ${cClient || '[nombre cliente]'}\n• Unique ID: ${cUid || '[unique ID no disponible]'}\n• Proyecto: ${cProj?.name || '[proyecto]'}\n• Unidad: ${cUnit || '[unidad]'}\n• Fecha del cobro: ${selPending?.fecha || '[fecha]'}\n\nPor favor contacta con el cliente.\n\nGracias,\nEquipo Finance — Prophero`);
    window.location.href = `mailto:propertycoach@prophero.com?subject=${subject}&body=${body}`;
    setToast(`Abriendo tu cliente de correo con el email a ${coachNamesText} redactado.`);
  };

  const submitPago = () => {
    setToast('Tanda añadida al timeline del proyecto.\nSi tiene ref. BC, se cruzará con el movimiento bancario.');
    setPProjSearch(''); setPImporte(''); setPFecha(''); setPTipo(''); setPDesc(''); setPVendedor(''); setPNotas('');
  };

  const coachContactLabel = useMemo(() => {
    if (coachNames.length === 0) return 'Property Coach';
    if (coachNames.length === 1) return coachNames[0];
    return `${coachNames[0]} +${coachNames.length - 1}`;
  }, [coachNames]);

  return (
    <div className="mw">
      <Toast message={toast} onHide={() => setToast('')} />

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 500 }}>Entrada manual de movimientos</div>
        <div style={{ fontSize: 12, color: 'var(--tx2)', marginTop: 2 }}>
          Para cobros sin unique ID y pagos de arras no registrados en el form de supply
        </div>
      </div>

      <div className="stabs">
        <div className={`st${activeTab === 'cobro' ? ' active' : ''}`} onClick={() => setActiveTab('cobro')}>
          <i className="ti ti-arrow-down" style={{ fontSize: 11 }}></i>
          Asignar cobro sin ID
          {pendingPayments.length > 0 && (
            <span className="badge br" style={{ fontSize: 10, padding: '1px 6px', marginLeft: 4 }}>{pendingPayments.length}</span>
          )}
        </div>
        <div className={`st${activeTab === 'pago' ? ' active' : ''}`} onClick={() => setActiveTab('pago')}>
          <i className="ti ti-arrow-up" style={{ fontSize: 11 }}></i>
          Añadir pago de arras
        </div>
      </div>

      {/* Cobro tab */}
      {activeTab === 'cobro' && (
        <div>
          <div className="abox aw">
            <i className="ti ti-alert-triangle" style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}></i>
            <div>
              <strong>{pendingPayments.length} cobros en BC sin unique ID identificado.</strong> Han entrado en cuenta pero el cliente no incluyó el código 000+UniqueID en el concepto. Asígnalos y notifica al Property Coach.
            </div>
          </div>

          <div className="fc">
            <div className="fct">
              Cobros pendientes de asignar · Business Central
              <span style={{ fontWeight: 400, fontSize: 11, color: 'var(--tx2)', marginLeft: 8 }}>
                {pendingPayments.length} registros
              </span>
            </div>
            <div className="pt-scroll">
              <table className="pt">
                <thead>
                  <tr><th>Fecha cobro</th><th>Ref. BC</th><th>Importe</th><th>Concepto</th><th></th></tr>
                </thead>
                <tbody>
                  {pendingPayments.length === 0 && (
                    <tr>
                      <td colSpan={5} style={{ textAlign: 'center', color: 'var(--tx3)', padding: '20px 0' }}>
                        No hay cobros pendientes sin Unique ID
                      </td>
                    </tr>
                  )}
                  {pendingPayments.map((p, i) => (
                    <tr key={i} className={selPending === p ? 'row-selected' : ''}>
                      <td style={{ whiteSpace: 'nowrap' }}>{p.fecha}</td>
                      <td style={{ color: 'var(--tx2)', fontSize: 12 }}>{p.noDocumento}</td>
                      <td style={{ fontWeight: 500, color: 'var(--gt)', whiteSpace: 'nowrap' }}>+€{p.importe}</td>
                      <td style={{ color: 'var(--tx2)', fontStyle: 'italic' }}>{p.concepto}</td>
                      <td>
                        <button className="asb" onClick={() => handleSelectPending(p)}>Asignar →</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="fc" style={{ borderColor: 'var(--bd2)' }}>
            <div className="fct">Asignar cobro a propiedad y cliente</div>
            <div className="sc">
              <span style={{ color: 'var(--tx2)' }}>Cobro seleccionado:</span>
              <span style={{ fontWeight: 500, marginLeft: 8 }}>
                {selPending ? `+€${selPending.importe} · ${selPending.fecha}` : '— selecciona un cobro de la lista de arriba —'}
              </span>
            </div>
            <div className="fg">
              <div className="fgg">
                <label>Fecha del cobro <span style={{ fontSize: 10, color: 'var(--tx3)', fontWeight: 400 }}>(viene de BC)</span></label>
                <input
                  type="text"
                  value={selPending?.fecha || ''}
                  readOnly
                  placeholder="Se rellena al seleccionar"
                  style={{ background: 'var(--bg2)', color: 'var(--tx2)' }}
                />
              </div>
              <div className="fgg">
                <label>Proyecto<span className="req">*</span> <span style={{ fontSize: 10, color: 'var(--tx3)', fontWeight: 400 }}>— busca por Project ID, nombre u operación</span></label>
                <ProjectDropdown
                  inputId="f-proj-search"
                  value={cProjSearch}
                  onChange={setCProjSearch}
                  onSelect={selectCProj}
                  placeholder="Ej: PROJ0510 o Daniel Miguel Alberic"
                  projectsData={projects}
                />
              </div>
              <div className="fgg">
                <label>
                  Unidad<span className="req">*</span>
                  {cProj && (
                    <span style={{ fontSize: 10, color: 'var(--tx3)', fontWeight: 400, marginLeft: 6 }}>
                      {pendingUnits.length} sin cliente · {(cProj.units ?? []).length} total
                    </span>
                  )}
                </label>
                <select value={cUnit} onChange={e => handleUnitChange(e.target.value)}>
                  <option value="">
                    {!cProj ? 'Selecciona un proyecto primero...' : pendingUnits.length === 0 ? 'No hay unidades sin cliente' : 'Seleccionar unidad sin cliente...'}
                  </option>
                  {pendingUnits.map(u => (
                    <option key={u.id ?? u.uniqueId} value={u.id ?? u.uniqueId}>
                      {u.address ?? u.name ?? u.uniqueId}
                      {u.toBeCollected ? `  · €${u.toBeCollected.toLocaleString('es-ES')}` : ''}
                    </option>
                  ))}
                </select>
                {cProj && pendingUnits.length === 0 && (
                  <div style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 3 }}>
                    Todas las unidades de este proyecto ya tienen cliente asignado.
                  </div>
                )}
                {selectedUnit && (
                  <div style={{ fontSize: 11, color: 'var(--tx2)', marginTop: 3 }}>
                    {selectedUnit.address && <span>{selectedUnit.address}</span>}
                    {selectedUnit.uniqueId && <span style={{ marginLeft: 6, color: 'var(--tx3)' }}>{selectedUnit.uniqueId}</span>}
                    {selectedUnit.toBeCollected > 0 && (
                      <span style={{ marginLeft: 6 }}>· To collect: <strong>€{selectedUnit.toBeCollected.toLocaleString('es-ES')}</strong></span>
                    )}
                  </div>
                )}
              </div>
              <div className="fgg">
                <label>
                  Nombre del cliente<span className="req">*</span>
                  {clientLoading && (
                    <span style={{ fontSize: 10, color: 'var(--tx3)', fontWeight: 400, marginLeft: 6 }}>
                      Buscando en HubSpot…
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  value={clientLoading ? '' : cClient}
                  onChange={e => setCClient(e.target.value)}
                  placeholder={clientLoading ? 'Buscando cliente en HubSpot…' : 'Nombre completo o razón social'}
                  disabled={clientLoading}
                  style={clientLoading ? { background: 'var(--bg2)', color: 'var(--tx3)' } : {}}
                />
              </div>
              <div className="fgg">
                <label>
                  Unique ID del cliente
                  {coachLookupLoading && (
                    <span style={{ fontSize: 10, color: 'var(--tx3)', fontWeight: 400, marginLeft: 6 }}>
                      Buscando Property Coach…
                    </span>
                  )}
                </label>
                <input type="text" value={cUid} onChange={e => setCUid(e.target.value)} placeholder="SP-XXX-XXX-XXXXXX" />
              </div>
              <div className="fgg full">
                <label>Notas internas</label>
                <textarea value={cNotes} onChange={e => setCNotes(e.target.value)}></textarea>
                <div className="note-warn">
                  <i className="ti ti-alert-circle" style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}></i>
                  <div>
                    <strong>Recuerda notificar a {coachContactLabel}.</strong>{' '}
                    Son responsables de que los clientes incluyan el código{' '}
                    <strong>{cUid?.trim() || 'Unique ID del cliente'}</strong>. Si el cliente no lo ha puesto, es su responsabilidad.
                    <br />
                    <button className="coach-btn" onClick={sendCoachEmail}>
                      <i className="ti ti-mail" style={{ fontSize: 13 }}></i>
                      Enviar aviso a {coachContactLabel}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="br2">
              <button className="bs" onClick={clearForm}>Cancelar</button>
              <button className="bp" onClick={submitCobro}>
                <i className="ti ti-check" style={{ fontSize: 12 }}></i>Asignar cobro
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pago tab */}
      {activeTab === 'pago' && (
        <div>
          <div className="abox ain">
            <i className="ti ti-info-circle" style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}></i>
            <div>Usa este formulario cuando supply haya pagado arras al vendedor sin completar el form oficial, o para añadir tandas previstas.</div>
          </div>
          <div className="fc">
            <div className="fct">Nuevo pago de arras al vendedor</div>
            <div className="fg">
              <div className="fgg">
                <label>Proyecto<span className="req">*</span> <span style={{ fontSize: 10, color: 'var(--tx3)', fontWeight: 400 }}>— busca por Project ID, nombre u operación</span></label>
                <ProjectDropdown
                  inputId="p-proj-search"
                  value={pProjSearch}
                  onChange={setPProjSearch}
                  onSelect={selectPProj}
                  placeholder="Ej: PROJ0522 o Cortes Moncofar"
                  projectsData={projects}
                />
              </div>
              <div className="fgg">
                <label>Importe<span className="req">*</span></label>
                <input type="number" value={pImporte} onChange={e => setPImporte(e.target.value)} placeholder="0.00" step="0.01" />
              </div>
              <div className="fgg">
                <label>Fecha del pago<span className="req">*</span></label>
                <input type="date" value={pFecha} onChange={e => setPFecha(e.target.value)} />
              </div>
              <div className="fgg">
                <label>Tipo de tanda<span className="req">*</span></label>
                <select value={pTipo} onChange={e => setPTipo(e.target.value)}>
                  <option value="">Seleccionar...</option>
                  <option>Tanda ya pagada (registrar en BC)</option>
                  <option>Tanda pendiente (próximo pago)</option>
                  <option>Tanda prevista (calendario futuro)</option>
                </select>
              </div>
              <div className="fgg">
                <label>Descripción de la tanda</label>
                <input type="text" value={pDesc} onChange={e => setPDesc(e.target.value)} placeholder="Ej: 2ª tanda · cierre bloque A" />
              </div>
              <div className="fgg">
                <label>Vendedor / propietario</label>
                <input type="text" value={pVendedor} onChange={e => setPVendedor(e.target.value)} placeholder="Nombre del vendedor del suelo" />
              </div>
              <div className="fgg full">
                <label>Notas internas</label>
                <textarea value={pNotas} onChange={e => setPNotas(e.target.value)} placeholder="Razón por la que no se usó el form oficial..."></textarea>
              </div>
            </div>
            <div className="br2">
              <button className="bs" onClick={() => { setPProjSearch(''); setPImporte(''); setPFecha(''); setPTipo(''); setPDesc(''); setPVendedor(''); setPNotas(''); }}>Cancelar</button>
              <button className="bp" onClick={submitPago}>
                <i className="ti ti-plus" style={{ fontSize: 12 }}></i>Añadir pago
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EntradaManual;
